import type { Org, OrgRole } from "./orgs.js";

/**
 * The platform API, as the browser sees it.
 *
 * Every call is same-origin and carries the `__Host-lagoon_owner` cookie
 * automatically — there is no token in JavaScript to steal, which is the whole
 * point of opaque server-side sessions. `credentials: "same-origin"` is
 * explicit rather than relying on the default, because a future change to that
 * default would silently sign every request out.
 */

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
  }

  get isUnauthenticated(): boolean {
    return this.status === 401;
  }

  /**
   * A publish the platform refused because content screening did not clear the
   * release. Matched on the code, never on the message: the message is written
   * for a person and will keep changing, the code is the contract.
   */
  get isScreeningRefusal(): boolean {
    return this.code === "screening_blocked";
  }

  /**
   * A refusal whose remedy is on the billing screen.
   *
   * Three codes, from three tickets, with one answer for the person reading it.
   * `seat_limit_reached` (LGN-71) is an invitation the plan has no seat for;
   * `billing_past_due` (LGN-72) is a write held back while a payment is
   * outstanding; `org_limit_reached` (LGN-121) is a team organization the plan
   * does not include. All three are well-formed requests refused by a number or
   * a state somebody can change, and in every case the next thing to do is open
   * Billing, which is why they are one predicate rather than three.
   *
   * Matched on the code for the same reason screening is: the sentences are
   * written for a person, relayed verbatim to Claude by the MCP surface, and
   * will keep being reworded.
   */
  get isBillingRefusal(): boolean {
    return (
      this.code === "seat_limit_reached" ||
      this.code === "billing_past_due" ||
      this.code === "org_limit_reached"
    );
  }

  /** The deployment holds no Maxio credentials. Normal in dev, never a fault. */
  get isBillingUnconfigured(): boolean {
    return this.code === "billing_not_configured";
  }
}

export interface ActionFailure {
  readonly message: string;
  /** Whether to frame the message as a screening refusal rather than a fault. */
  readonly screening: boolean;
  /**
   * Whether the remedy is on the billing screen, so the caller can offer a
   * link there instead of leaving the reader with a number and no next step.
   */
  readonly billing: boolean;
}

/**
 * What to show when a publish or a rollback fails.
 *
 * Screening refusals are worth separating from everything else in this screen,
 * because they are the only failure here that is not a fault: nothing is
 * broken, the release is simply not allowed to go live, and the next step is to
 * change the files rather than to try again. Anything unrecognised falls back
 * to the caller's wording, so a network failure never renders as a screening
 * verdict.
 */
export function describeActionFailure(caught: unknown, fallback: string): ActionFailure {
  if (!(caught instanceof ApiError)) return { message: fallback, screening: false, billing: false };
  return {
    message: caught.message,
    screening: caught.isScreeningRefusal,
    billing: caught.isBillingRefusal,
  };
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  // Built as a Headers instance rather than spread into an object literal:
  // HeadersInit may legitimately be an array of pairs, and spreading that into
  // an object silently produces numeric keys instead of headers.
  const headers = new Headers(init.headers);
  headers.set("accept", "application/json");
  if (init.body !== undefined) headers.set("content-type", "application/json");

  const response = await fetch(path, {
    ...init,
    credentials: "same-origin",
    headers,
  });

  if (response.status === 204) return undefined as T;

  const payload: unknown = await response.json().catch(() => null);

  if (!response.ok) {
    throw new ApiError(
      response.status,
      readString(payload, "error") ?? "request_failed",
      readString(payload, "message") ?? `Request failed (${response.status})`,
    );
  }

  // The response body is not validated at runtime. These shapes are serialised
  // from zod schemas server-side, so the risk here is a deploy skew between
  // client and server rather than untrusted input — worth a shared schema
  // package if this surface grows, but not worth runtime parsing today.
  return payload as T;
}

function readString(value: unknown, key: string): string | undefined {
  if (typeof value !== "object" || value === null) return undefined;
  const found: unknown = Reflect.get(value, key);
  return typeof found === "string" ? found : undefined;
}

// --- types mirroring the API responses --------------------------------------

export interface Site {
  id: string;
  slug: string;
  name: string;
  publishedDeployId: string | null;
  publicMode: boolean;
  suspended: boolean;
  createdAt: string;
}

/**
 * The four facts a dunning banner is drawn from, and the whole of what a role
 * that may not see billing is told about money (LGN-97).
 *
 * Named separately from `BillingDunning` below because two endpoints carry it:
 * `GET /api/billing`, for the roles that may see billing, and
 * `GET /api/orgs/:orgId/entitlements`, which every member may read and which is
 * where the shell gets the banner from. The banner explains why creating a site
 * or uploading a release is refused, so hiding it from the people being refused
 * would be hiding the explanation from its audience.
 *
 * `state` is `string` rather than the union on purpose: see `BillingDunning`.
 */
export interface DunningFacts {
  /** `none` | `warning` | `restricted` | `suspended`, from the org's column. */
  readonly state: string;
  /** Whether creating sites, uploading and inviting are refused right now. */
  readonly createBlocked: boolean;
  /** Whole days until they are, or null when there is no clock running. */
  readonly daysUntilBlock: number | null;
  /** Suspended for money specifically, rather than by an operator. */
  readonly suspendedByBilling: boolean;
}

/** What one organization is allowed to do, and how much of it is left. */
export interface Entitlements {
  orgId: string;
  role: OrgRole;
  plan: string;
  suspended: boolean;
  sites: { used: number; quota: number; remaining: number };
  storage: { usedBytes: number; quotaBytes: number };
  auditExport: boolean;
  /** Readable by every member, which is what keeps the banner universal. */
  dunning: DunningFacts;
}

/**
 * Where the organisation stands: plan, trial, and the three meters (LGN-170).
 *
 * The per-org half of what `get_account` tells a model, from the same domain
 * function, so the overview panel and the tool cannot report different numbers
 * for the same organisation. Narrower than `Entitlements` on the feature flags
 * and wider on the two things that route never carried: the trial's end and
 * the seat ceiling.
 *
 * `summary` is the platform's own one-liner for the org. Not rendered as the
 * headline here, because a screen has room to lay the figures out, but it is
 * the sentence Claude reads out and it is worth having on the same payload the
 * screen drew: if the two ever disagree, the difference is visible.
 */
export interface OrgAccount {
  readonly orgId: string;
  readonly name: string;
  readonly kind: "personal" | "team";
  readonly role: OrgRole;
  /** The plan code, lowercase, as the column holds it. */
  readonly plan: string;
  /** ISO instant the trial expires, or null when this org is not trialling. */
  readonly trialEndsAt: string | null;
  readonly suspended: boolean;
  readonly sites: { readonly used: number; readonly quota: number; readonly remaining: number };
  readonly storage: { readonly usedBytes: number; readonly quotaBytes: number };
  /** Filled seats against the plan's ceiling. `limit`, not `quota`, as the domain names it. */
  readonly seats: { readonly used: number; readonly limit: number };
  readonly maxTeamOrgs: number;
  readonly summary: string;
}

/**
 * One row of the organisation's audit trail (LGN-146).
 *
 * Deliberately narrower than the admin console's `AuditEvent`: no actor id, no
 * IP, no user agent, no request id, no token jti, and on a row an operator
 * wrote, no reason, no state and no staff address either. The platform decides
 * that in `orgAuditView`, so this type is what actually arrives rather than a
 * subset the screen happens to render.
 */
export interface OrgAuditEvent {
  readonly id: number;
  readonly occurredAt: string;
  /** `owner`, `staff`, `admin`, `system`, `billing` or `viewer`. */
  readonly actorType: string;
  /** Null on every row the organisation did not write itself. */
  readonly actorEmail: string | null;
  readonly action: string;
  readonly targetType: string | null;
  readonly targetId: string | null;
  readonly siteId: string | null;
  readonly reason: string | null;
  readonly before: unknown;
  readonly after: unknown;
  /** The platform held something back on this row, and says so. */
  readonly withheld: boolean;
}

export interface OrgAuditPage {
  readonly events: OrgAuditEvent[];
  /** Keyset cursor for the next, older page. Null at the end of the trail. */
  readonly nextBefore: number | null;
}

export interface Member {
  ownerId: string;
  email: string;
  name: string | null;
  role: OrgRole;
  invitedByOwnerId: string | null;
  joinedAt: string;
}

/**
 * An invitation that has been sent and not yet answered.
 *
 * No token, and there never will be one: the platform returns it exactly once,
 * into the email body. A pending list that carried it would turn "can read the
 * dashboard" into "can join any org anybody has been invited to".
 */
export interface Invitation {
  id: string;
  email: string;
  role: OrgRole;
  invitedByOwnerId: string | null;
  createdAt: string;
  expiresAt: string;
}

export type DeployState =
  | "pending"
  | "uploading"
  | "processing"
  | "ready"
  | "failed"
  | "superseded";

export interface Deploy {
  id: string;
  state: DeployState;
  fileCount: number;
  totalBytes: number;
  error: string | null;
  createdAt: string;
  readyAt: string | null;
  isPublished: boolean;
}

export interface AccessEvent {
  sessionId: string;
  /** Null for a guest: possession of a link proves no address. */
  email: string | null;
  method: "passcode" | "guest_pass" | "owner";
  guestLabel: string | null;
  startedAt: string;
  lastSeenAt: string;
  expiresAt: string;
  revokedAt: string | null;
  revokedReason: string | null;
  /** Above zero means they kept trying after losing access. */
  deniedCount: number;
  lastDeniedAt: string | null;
  ip: string | null;
  userAgent: string | null;
  active: boolean;
  /** Mail to this address is not arriving, so a resent code will not either. */
  bouncing: boolean;
  bounceReason: string | null;
}

export interface AccessLog {
  summary: {
    distinctVisitors: number;
    totalSessions: number;
    lockedOut: number;
    /** Bouncing addresses among the rows returned, not across the site. */
    bouncingOnPage: number;
    truncated: boolean;
  };
  events: AccessEvent[];
}

export interface GuestPass {
  id: string;
  label: string;
  expiresAt: string;
  maxUses: number | null;
  uses: number;
  active: boolean;
  revokedAt: string | null;
  createdAt: string;
}

export interface CreatedPass {
  id: string;
  label: string;
  expiresAt: string;
  maxUses: number | null;
  /** Returned once and never again. */
  url: string;
}

interface Delivery {
  id: number;
  event: string;
  channel: "email" | "webhook";
  target: string;
  status: "sent" | "failed";
  statusCode: number | null;
  error: string | null;
  createdAt: string;
}

export interface NotificationSettings {
  /** Null follows the account setting; true and false override it. */
  notifyOnDeploy: boolean | null;
  accountNotifyOnDeploy: boolean;
  effectiveNotifyOnDeploy: boolean;
  webhookUrl: string | null;
  hasWebhookSecret: boolean;
  deliveries: Delivery[];
}

interface ChangedFile {
  path: string;
  change: "added" | "removed" | "modified";
  sizeBytes: number;
  previousSizeBytes: number | null;
}

export interface DeployDiff {
  baseDeployId: string | null;
  headDeployId: string;
  added: number;
  removed: number;
  modified: number;
  unchanged: number;
  files: ChangedFile[];
  truncated: boolean;
  summary: string;
}

export interface AllowlistEntry {
  id: string;
  email: string;
  note: string | null;
  createdAt: string;
  /**
   * Still allowed in, still not receiving passcodes.
   *
   * Two separate facts: the provider told us mail to this address bounces,
   * and nothing about the allowlist changed. Shown so the owner can act on
   * the address rather than wonder why somebody they invited cannot get in.
   */
  bouncing: boolean;
  bounceReason: string | null;
  bouncingSince: string | null;
}

export interface DomainRule {
  id: string;
  domain: string;
  note: string | null;
  createdAt: string;
}

export interface GroupSummary {
  id: string;
  name: string;
  description: string | null;
  memberCount: number;
  siteCount: number;
  createdAt: string;
}

export interface GroupDetail {
  id: string;
  name: string;
  description: string | null;
  createdAt: string;
  members: { email: string; note: string | null; addedAt: string }[];
  sites: { id: string; slug: string; name: string }[];
}

export interface SiteGroup {
  id: string;
  name: string;
  memberCount: number;
  attachedAt: string;
}

// --- billing -----------------------------------------------------------------

/**
 * The three non-secret strings the browser needs to draw Maxio's card fields.
 *
 * Null on the summary when there is nothing to draw, which is the state dev2
 * and CI are in. The publishable key is meant for a browser; the API key never
 * leaves the platform.
 */
export interface BillingCheckoutConfig {
  readonly publicKey: string;
  /** `https://<site>.chargify.com`: where the hosted fields post the card. */
  readonly serverHost: string;
  /** The exact script URL, and the only one the dashboard's CSP allows. */
  readonly scriptUrl: string;
  /**
   * Which of the site's gateways the card is tokenised against. Null means the
   * site default, which is only right for a site with one gateway.
   */
  readonly gatewayHandle: string | null;
}

/**
 * Where this organisation stands with dunning, as LGN-72 decided it.
 *
 * Every field is read, none is re-derived. `createBlocked` is the boolean the
 * screens branch on and `daysUntilBlock` is the countdown they print: day seven
 * is `effectiveDunningState` subtracting a stored timestamp on the server, and
 * a bundle that worked it out from `dunningAt` would be a second opinion about
 * when creating stops working.
 *
 * `state` is typed as `string` rather than the union on purpose. It crosses a
 * network boundary from a service that may be a deploy ahead of this bundle,
 * and an unrecognised value must render as silence rather than as a crash on a
 * page every signed-in customer reaches. See `describeDunning`.
 */
export interface BillingDunning extends DunningFacts {
  /** The collapsed Maxio state: trialing, active, past_due, delinquent, ended. */
  readonly subscriptionState: string;
  /** The raw Maxio subscription state from the newest event that named one. */
  readonly maxioState: string | null;
  /** False only once the customer has stopped being one. True through dunning. */
  readonly keepsEntitlements: boolean;
  readonly since: string | null;
  readonly lastEvent: string | null;
  /** When Maxio last said this customer is in dunning. */
  readonly dunningAt: string | null;
}

/**
 * The same numbers the limit errors cite, from the same functions that enforce
 * them. `viewers.used` is null because nothing counts them yet; it renders as
 * "not measured", never as zero.
 */
export interface BillingMeters {
  readonly sites: { used: number; quota: number; remaining: number };
  readonly storage: { usedBytes: number; quotaBytes: number };
  readonly seats: { used: number; quota: number };
  readonly viewers: { used: number | null; quota: number };
}

/**
 * One period's price, exactly as the synced catalog holds it (LGN-127).
 *
 * Integer minor units and the ISO 4217 code they are in, never a formatted
 * string: formatting is the reader's job and the currency decides how many
 * decimal places it has. The platform sends this only when it knows both, so
 * there is no half-price to guard against here.
 */
export interface BillingPlanPrice {
  readonly priceCents: number;
  readonly currency: string;
}

/**
 * What each period of a plan costs, or null where nothing is known.
 *
 * Null is the honest answer in two cases the checkout screen has to tell apart
 * from a cheap plan: the catalog has no synced price for that period, and the
 * plan cannot be bought for that period at all. Neither may be rendered as a
 * zero or a placeholder; see `describeCheckoutAmount` in `billing.ts`.
 */
interface BillingPlanPrices {
  readonly monthly: BillingPlanPrice | null;
  readonly annual: BillingPlanPrice | null;
}

export interface BillingPlanOption {
  readonly code: string;
  readonly name: string;
  readonly rank: number;
  readonly selfServe: boolean;
  readonly current: boolean;
  /** False until the Maxio catalog exists: offering it would offer a 503. */
  readonly available: boolean;
  /**
   * Optional because the SPA in a browser can be one deploy behind the platform
   * answering it, and a screen that threw on a missing field would take the
   * whole billing page down to hide a price. Absent reads as "no price known",
   * which is the same path the null prices already take.
   */
  readonly prices?: BillingPlanPrices | undefined;
}

export interface BillingSummary {
  readonly orgId: string;
  readonly role: OrgRole;
  /** False means this deployment holds no Maxio credentials. Not an error. */
  readonly configured: boolean;
  readonly plan: { code: string; name: string; rank: number; selfServe: boolean };
  readonly suspended: boolean;
  readonly subscription: {
    id: string;
    customerId: string | null;
    trialEndsAt: string | null;
  } | null;
  readonly dunning: BillingDunning;
  readonly meters: BillingMeters;
  readonly plans: readonly BillingPlanOption[];
  readonly checkout: BillingCheckoutConfig | null;
}

export interface Invoice {
  readonly uid: string;
  readonly number: string | null;
  readonly status: string | null;
  readonly issueDate: string | null;
  readonly dueDate: string | null;
  readonly totalAmount: string | null;
  readonly currency: string | null;
  /** Maxio's own invoice page, which is where the PDF is downloaded from. */
  readonly publicUrl: string | null;
}

/** Maxio's arithmetic, in integer cents. Never recomputed on this side. */
export interface ProrationPreview {
  readonly proratedAdjustmentInCents: number;
  readonly chargeInCents: number;
  readonly paymentDueInCents: number;
  readonly creditAppliedInCents: number;
}

export type BillingPeriod = "monthly" | "annual";

export interface UploadTicket {
  deployId: string;
  uploadUrl: string;
  expiresAt: string;
  method: string;
  headers: Record<string, string>;
}

// --- auth --------------------------------------------------------------------

export const api = {
  me: () =>
    request<{
      owner: { id: string; email: string; isStaff: boolean; notifyOnDeploy: boolean };
    }>("/api/auth/me"),

  signOut: () => request<{ status: string }>("/api/auth/signout", { method: "POST" }),

  // --- sites -----------------------------------------------------------------

  /**
   * The sites in one org. Omitting `orgId` means the personal org, which is
   * the platform's default rather than this client's.
   */
  listSites: (orgId?: string) =>
    request<{ orgId: string; role: OrgRole; sites: Site[] }>(
      orgId === undefined ? "/api/sites" : `/api/sites?orgId=${encodeURIComponent(orgId)}`,
    ),

  createSite: (slug: string, name: string, orgId?: string) =>
    request<{ site: Site }>("/api/sites", {
      method: "POST",
      body: JSON.stringify(orgId === undefined ? { slug, name } : { slug, name, orgId }),
    }),

  getSite: (id: string) => request<{ site: Site; deploys: Deploy[] }>(`/api/sites/${id}`),

  checkSlug: (slug: string) =>
    request<{ available: boolean; reason: string | null }>(
      `/api/slugs/${encodeURIComponent(slug)}/available`,
    ),

  // --- organizations ---------------------------------------------------------

  listOrgs: () => request<{ orgs: Org[] }>("/api/orgs"),

  createOrg: (name: string) =>
    request<{ id: string; name: string; kind: "team"; role: OrgRole }>("/api/orgs", {
      method: "POST",
      body: JSON.stringify({ name }),
    }),

  getEntitlements: (orgId: string) => request<Entitlements>(`/api/orgs/${orgId}/entitlements`),

  /** Plan, trial and the three meters, as `get_account` reports them (LGN-170). */
  getOrgAccount: (orgId: string) => request<OrgAccount>(`/api/orgs/${orgId}/account`),

  /**
   * A page of the organisation's audit trail, newest first (LGN-146).
   *
   * Keyset, not offset: the table is append-only, so ids are monotonic and an
   * offset drifts as rows arrive underneath the reader. `before` is the id the
   * previous page ended at.
   *
   * `admin` and above, and gated on `entitlements.auditExport`, both decided
   * server-side. A caller whose plan has lapsed gets a 403 carrying the
   * platform's sentence, which is what the screen shows.
   */
  orgAudit: (orgId: string, options: { limit: number; before?: number }) =>
    request<OrgAuditPage>(
      `/api/orgs/${orgId}/audit?limit=${String(options.limit)}` +
        (options.before === undefined ? "" : `&before=${String(options.before)}`),
    ),

  listMembers: (orgId: string) =>
    request<{ orgId: string; role: OrgRole; seats: number; members: Member[] }>(
      `/api/orgs/${orgId}/members`,
    ),

  changeMemberRole: (orgId: string, ownerId: string, role: OrgRole) =>
    request<{ orgId: string; ownerId: string; role: OrgRole }>(
      `/api/orgs/${orgId}/members/${ownerId}`,
      { method: "PATCH", body: JSON.stringify({ role }) },
    ),

  removeMember: (orgId: string, ownerId: string, reason?: string) =>
    request<{ removed: boolean }>(`/api/orgs/${orgId}/members/${ownerId}`, {
      method: "DELETE",
      body: JSON.stringify(reason === undefined ? {} : { reason }),
    }),

  listInvitations: (orgId: string) =>
    request<{ invitations: Invitation[] }>(`/api/orgs/${orgId}/invitations`),

  /**
   * Invite an address. `emailed: false` means the invitation exists but the
   * message did not go out, which is a thing to tell the admin rather than to
   * hide behind a green tick.
   */
  invite: (orgId: string, email: string, role: OrgRole) =>
    request<{ id: string; email: string; role: OrgRole; expiresAt: string; emailed: boolean }>(
      `/api/orgs/${orgId}/invitations`,
      { method: "POST", body: JSON.stringify({ email, role }) },
    ),

  revokeInvitation: (orgId: string, invitationId: string) =>
    request<{ revoked: boolean }>(`/api/orgs/${orgId}/invitations/${invitationId}`, {
      method: "DELETE",
    }),

  /**
   * Spend an invitation.
   *
   * POST and not GET, matching the platform: a link a mail scanner prefetches
   * must not burn the invitation. A signed-out caller gets a 303 to
   * `/auth/login`, which `fetch` follows into an HTML page and this client
   * surfaces as a failure; the acceptance screen sends the person to sign in
   * itself rather than relying on that.
   */
  acceptInvitation: (token: string) =>
    request<{ orgId: string; orgName: string; role: OrgRole; alreadyMember: boolean }>(
      `/invitations/${encodeURIComponent(token)}/accept`,
      { method: "POST" },
    ),

  // --- deploys ---------------------------------------------------------------

  createUpload: (siteId: string) =>
    request<UploadTicket>(`/api/sites/${siteId}/uploads`, { method: "POST" }),

  processDeploy: (deployId: string) =>
    request<{ deployId: string; state: string }>(`/api/deploys/${deployId}/process`, {
      method: "POST",
    }),

  getDeploy: (deployId: string) =>
    request<{
      deployId: string;
      state: DeployState;
      fileCount: number;
      error: string | null;
      /** Null until the release is ready, and again once it is reclaimed. */
      previewUrl: string | null;
    }>(`/api/deploys/${deployId}`),

  publish: (siteId: string, deployId: string) =>
    request<{ published: string; previous: string | null }>(`/api/sites/${siteId}/publish`, {
      method: "POST",
      body: JSON.stringify({ deployId }),
    }),

  rollback: (siteId: string, deployId: string, reason: string) =>
    request<{ published: string; previous: string | null }>(`/api/sites/${siteId}/rollback`, {
      method: "POST",
      body: JSON.stringify({ deployId, reason }),
    }),

  // --- allowlist -------------------------------------------------------------

  listAllowlist: (siteId: string) =>
    request<{ entries: AllowlistEntry[] }>(`/api/sites/${siteId}/allowlist`),

  addToAllowlist: (siteId: string, email: string, note?: string) =>
    request<{ email: string; added: boolean }>(`/api/sites/${siteId}/allowlist`, {
      method: "POST",
      body: JSON.stringify(note === undefined ? { email } : { email, note }),
    }),

  removeFromAllowlist: (siteId: string, entryId: string) =>
    request<{ removed: string }>(`/api/sites/${siteId}/allowlist/${entryId}`, {
      method: "DELETE",
    }),

  // --- domain rules ------------------------------------------------------------

  listDomains: (siteId: string) =>
    request<{ domains: DomainRule[] }>(`/api/sites/${siteId}/domains`),

  allowDomain: (siteId: string, domain: string) =>
    request<{ domain: string; added: boolean }>(`/api/sites/${siteId}/domains`, {
      method: "POST",
      body: JSON.stringify({ domain }),
    }),

  removeDomain: (siteId: string, domain: string) =>
    request<{ domain: string; removed: boolean; sessionsRevoked: number }>(
      `/api/sites/${siteId}/domains/${encodeURIComponent(domain)}`,
      { method: "DELETE" },
    ),

  // --- groups ------------------------------------------------------------------

  listGroups: () => request<{ groups: GroupSummary[] }>("/api/groups"),

  createGroup: (name: string, description?: string) =>
    request<{ id: string; name: string; created: boolean }>("/api/groups", {
      method: "POST",
      body: JSON.stringify(description === undefined ? { name } : { name, description }),
    }),

  getGroup: (groupId: string) => request<GroupDetail>(`/api/groups/${groupId}`),

  deleteGroup: (groupId: string) =>
    request<{ deleted: boolean; sessionsRevoked: number }>(`/api/groups/${groupId}`, {
      method: "DELETE",
    }),

  addGroupMember: (groupId: string, email: string) =>
    request<{ email: string; added: boolean }>(`/api/groups/${groupId}/members`, {
      method: "POST",
      body: JSON.stringify({ email }),
    }),

  removeGroupMember: (groupId: string, email: string) =>
    request<{ email: string; removed: boolean; sessionsRevoked: number }>(
      `/api/groups/${groupId}/members`,
      { method: "DELETE", body: JSON.stringify({ email }) },
    ),

  listSiteGroups: (siteId: string) =>
    request<{ groups: SiteGroup[] }>(`/api/sites/${siteId}/groups`),

  attachGroup: (siteId: string, groupId: string) =>
    request<{ group: string; added: boolean }>(`/api/sites/${siteId}/groups`, {
      method: "POST",
      body: JSON.stringify({ groupId }),
    }),

  detachGroup: (siteId: string, groupId: string) =>
    request<{ group: string; removed: boolean; sessionsRevoked: number }>(
      `/api/sites/${siteId}/groups/${groupId}`,
      { method: "DELETE" },
    ),

  // --- who has actually been in ----------------------------------------------

  getAccessLog: (siteId: string) => request<AccessLog>(`/api/sites/${siteId}/access`),

  endSession: (siteId: string, sessionId: string) =>
    request<{ ended: boolean }>(`/api/sites/${siteId}/access/${sessionId}`, { method: "DELETE" }),

  // --- guest passes ----------------------------------------------------------

  listPasses: (siteId: string) => request<{ passes: GuestPass[] }>(`/api/sites/${siteId}/passes`),

  createPass: (
    siteId: string,
    input: { label: string; expiresInHours: number; maxUses: number | null },
  ) =>
    request<CreatedPass>(`/api/sites/${siteId}/passes`, {
      method: "POST",
      body: JSON.stringify(input),
    }),

  revokePass: (siteId: string, passId: string, reason: string) =>
    request<{ revoked: boolean; sessionsRevoked: number }>(
      `/api/sites/${siteId}/passes/${passId}`,
      { method: "DELETE", body: JSON.stringify({ reason }) },
    ),

  // --- notifications ---------------------------------------------------------

  getNotifications: (siteId: string) =>
    request<NotificationSettings>(`/api/sites/${siteId}/notifications`),

  updateNotifications: (
    siteId: string,
    // `notifyOnDeploy: null` means "follow the account"; omitting it leaves
    // the setting alone. The two are deliberately different.
    input: { notifyOnDeploy?: boolean | null; webhookUrl?: string | null },
  ) =>
    request<{
      notifyOnDeploy: boolean | null;
      accountNotifyOnDeploy: boolean;
      effectiveNotifyOnDeploy: boolean;
      webhookUrl: string | null;
      webhookSecret: string | null;
    }>(`/api/sites/${siteId}/notifications`, { method: "PUT", body: JSON.stringify(input) }),

  setAccountNotifications: (notifyOnDeploy: boolean) =>
    request<{ notifyOnDeploy: boolean }>("/api/account/notifications", {
      method: "PUT",
      body: JSON.stringify({ notifyOnDeploy }),
    }),

  // --- billing ---------------------------------------------------------------

  /**
   * Everything the billing screen renders, from Postgres alone.
   *
   * No Maxio call happens behind this, configured or not, which is why it is
   * safe to poll after a 202: refetching costs a query and never a request to
   * a payment provider.
   */
  getBilling: (orgId?: string) =>
    request<BillingSummary>(
      orgId === undefined ? "/api/billing" : `/api/billing?orgId=${encodeURIComponent(orgId)}`,
    ),

  /**
   * Maxio's invoices, fetched now and stored nowhere.
   *
   * Unconfigured, and an org with no subscription, both answer an empty list
   * rather than an error, so the caller has one shape to render.
   */
  getInvoices: (orgId?: string) =>
    request<{ orgId: string; role: OrgRole; configured: boolean; invoices: Invoice[] }>(
      orgId === undefined
        ? "/api/billing/invoices"
        : `/api/billing/invoices?orgId=${encodeURIComponent(orgId)}`,
    ),

  /**
   * Spend a Maxio.js token: subscribe, or replace the card on a subscription.
   *
   * The token is minted in the browser against Maxio's origin and is worth
   * nothing once spent; the card itself never reaches this bundle, let alone
   * the platform. Answers 202 on a new subscription, because the plan moves
   * when the webhook lands and not when this returns.
   */
  checkout: (input: { orgId?: string; token: string; plan?: string; period: BillingPeriod }) =>
    request<{
      orgId: string;
      action: "subscribed" | "card_updated";
      requestedPlan?: string;
      subscription: { id: string; state: string | null } | null;
    }>("/api/billing/checkout-token", { method: "POST", body: JSON.stringify(input) }),

  /** Maxio's own proration arithmetic. Changes nothing. */
  previewPlan: (input: { orgId?: string; plan: string; period: BillingPeriod }) =>
    request<{
      orgId: string;
      from: string;
      to: string;
      period: BillingPeriod;
      preview: ProrationPreview;
    }>("/api/billing/plan", { method: "POST", body: JSON.stringify({ ...input, preview: true }) }),

  /**
   * Ask for a plan change. 202: requested, not applied.
   *
   * The response names `requestedPlan`, never the current one, and the screen
   * repeats that distinction rather than smoothing it over.
   */
  changePlan: (input: { orgId?: string; plan: string; period: BillingPeriod }) =>
    request<{
      orgId: string;
      from: string;
      requestedPlan: string;
      period: BillingPeriod;
      subscription: { id: string; state: string | null } | null;
    }>("/api/billing/plan", { method: "POST", body: JSON.stringify({ ...input, preview: false }) }),

  /**
   * Cancel at the end of the paid period, with the optional exit survey.
   *
   * Optional because a required reason on a cancellation form is a dark
   * pattern: somebody who has decided to leave should not have to explain
   * themselves to be allowed to.
   */
  cancelSubscription: (input: { orgId?: string; reason?: string }) =>
    request<{
      orgId: string;
      cancelAtPeriodEnd: boolean;
      plan: string;
      periodEndsAt: string | null;
    }>("/api/billing/cancel", { method: "POST", body: JSON.stringify(input) }),

  // --- what a release would change -------------------------------------------

  getDiff: (deployId: string) => request<DeployDiff>(`/api/deploys/${deployId}/diff`),
};

/**
 * Send a release archive straight to Blob.
 *
 * Deliberately not through the API: this PUT goes to a short-lived SAS URL, so
 * the bytes never cross Front Door and are not subject to its 2 GB body cap,
 * its origin timeout, or its egress meter.
 */
export async function uploadArchive(ticket: UploadTicket, file: File): Promise<void> {
  const response = await fetch(ticket.uploadUrl, {
    method: ticket.method,
    headers: ticket.headers,
    body: file,
  });
  if (!response.ok) {
    throw new ApiError(response.status, "upload_failed", `Upload failed (${response.status})`);
  }
}

/**
 * The code an export that died mid-stream is reported under (LGN-145).
 *
 * Its own code rather than a sentence to match on, for the reason every other
 * refusal in this file has one: the wording is written for a person and will be
 * reworded, and this is the one export failure whose remedy differs from "read
 * what the platform said". It is not a platform code, because the platform
 * never got to say anything: by the time the connection broke it had already
 * answered 200 and started writing the archive.
 */
export const EXPORT_INTERRUPTED = "export_interrupted";

/** A site's content, as the browser now holds it. */
export interface SiteExportArchive {
  /** The whole archive, in memory, ready to hand to a download. */
  readonly blob: Blob;
  /** What to call the saved file: the platform's own `Content-Disposition`. */
  readonly filename: string;
  /** `?all=1` filled its page, so this is one page of the history (LGN-115). */
  readonly truncated: boolean;
}

/**
 * Fetch `GET /api/sites/:id/export` and hold the archive.
 *
 * **Why not `<a href=… download>`.** That is one line and it is wrong here, in
 * two ways that both end with somebody believing they have their content when
 * they do not. A refusal on this route is JSON with a status: the limiter's
 * `rate_limited`, a suspended site's 403, a site with no live release, an
 * `?all=1` too large to build. A browser navigating to the link either saves
 * that JSON under a `.zip` name or shows it as a page, and either way the
 * platform's sentence never reaches the screen the button is on. And a stream
 * that fails *after* the 200 is saved as a short archive with no warning at
 * all, because the response has no `Content-Length` to fall short of.
 *
 * Reading the body here fixes both. A non-2xx is parsed as the refusal it is,
 * and a body that stops early rejects out of `blob()` rather than resolving:
 * that rejection becomes {@link EXPORT_INTERRUPTED}, and nothing is saved.
 *
 * The cost is that the archive is buffered, up to the route's gigabyte ceiling.
 * A `Blob` is not necessarily resident memory (browsers spill large ones to
 * disk), and paying that is the deliberate trade for a download that cannot
 * lie about having succeeded.
 */
export async function fetchSiteExport(
  site: { id: string; slug: string },
  all: boolean,
): Promise<SiteExportArchive> {
  const response = await fetch(`/api/sites/${site.id}/export${all ? "?all=1" : ""}`, {
    credentials: "same-origin",
    headers: { accept: "application/zip" },
  });

  if (!response.ok) {
    const payload: unknown = await response.json().catch(() => null);
    throw new ApiError(
      response.status,
      readString(payload, "error") ?? "request_failed",
      readString(payload, "message") ?? `Request failed (${response.status})`,
    );
  }

  let blob: Blob;
  try {
    blob = await response.blob();
  } catch {
    // The status was 200 and stays 200: what failed is the body, and saying
    // otherwise would put a status on the screen that no server ever sent.
    throw new ApiError(
      response.status,
      EXPORT_INTERRUPTED,
      "The download stopped part way through, so nothing was saved. Your site is unchanged; try again.",
    );
  }

  return {
    blob,
    filename:
      filenameFrom(response.headers.get("content-disposition")) ?? exportFilename(site.slug, all),
    // Same fact the archive's own EXPORT-TRUNCATED.txt carries, read here so
    // the screen can say it before anybody opens the ZIP.
    truncated: response.headers.get("x-lagoon-export-truncated") === "1",
  };
}

/**
 * The filename out of `Content-Disposition`, when there is one.
 *
 * Quoted form only, which is the only form this route sends. Anything with a
 * path separator in it is refused rather than repaired: a `download` attribute
 * is not a place to be inventive about what a server said.
 */
function filenameFrom(header: string | null): string | null {
  const match = header === null ? null : /filename="([^"]+)"/.exec(header);
  const name = match?.[1];
  if (name === undefined || name.includes("/") || name.includes("\\")) return null;
  return name;
}

/** What the platform would have called it. Mirrors `exportFilename` server-side. */
function exportFilename(slug: string, all: boolean): string {
  return all ? `${slug}-all-releases.zip` : `${slug}.zip`;
}
